import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  GroupCarrierDetail,
  GroupCarriers,
  GroupMe,
  GroupsOverviewResponse,
  GroupSharingSettings,
  GroupSyncResult,
} from '@carrierbridge/shared';
import api from '@/lib/api';

const GROUP_KEY = ['group'] as const;

/**
 * Le groupe de MA société : son corporate (si c'est une filiale) ou ses filiales (si c'est
 * un corporate). Le lien vient du portail Pool4ward ; ici il se lit, jamais il ne se pose.
 * Une société qui n'est dans aucun groupe reçoit `{ corporate: null, filiales: [] }`.
 */
export function useGroup() {
  return useQuery<GroupMe>({
    queryKey: [...GROUP_KEY, 'me'],
    queryFn: async () => (await api.get<GroupMe>('/groups/me')).data,
    staleTime: 60_000,
  });
}

/** Les transporteurs du groupe, regroupés par n° de TVA (lecture seule). */
export function useGroupCarriers(enabled = true) {
  return useQuery<GroupCarriers>({
    queryKey: [...GROUP_KEY, 'carriers'],
    queryFn: async () => (await api.get<GroupCarriers>('/carriers/group')).data,
    staleTime: 30_000,
    enabled,
  });
}

/** Fiche en lecture seule d'un transporteur du groupe ; rien ne part tant que `id` est nul. */
export function useGroupCarrier(id: string | null) {
  return useQuery<GroupCarrierDetail>({
    queryKey: [...GROUP_KEY, 'carrier', id],
    queryFn: async () => (await api.get<GroupCarrierDetail>(`/carriers/group/${id}`)).data,
    enabled: id !== null,
  });
}

/** Console superadmin : les groupes repris du portail. */
export function useGroupsOverview() {
  return useQuery<GroupsOverviewResponse>({
    queryKey: [...GROUP_KEY, 'overview'],
    queryFn: async () => (await api.get<GroupsOverviewResponse>('/groups')).data,
  });
}

/** Reprend maintenant le groupe de toutes les sociétés liées, sans attendre les 5 minutes. */
export function useResyncGroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<GroupSyncResult>('/groups/sync')).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: GROUP_KEY }),
  });
}

const SHARING_KEY = [...GROUP_KEY, 'sharing'] as const;

/** Réglage « partager toutes les grilles » de ma société (exploitation, prospects). */
export function useGroupSharing(enabled = true) {
  return useQuery<GroupSharingSettings>({
    queryKey: SHARING_KEY,
    queryFn: async () => (await api.get<GroupSharingSettings>('/carriers/group-sharing')).data,
    staleTime: 30_000,
    enabled,
  });
}

/** Partage (ou retire) toutes les grilles d'une famille ; vaut aussi pour les transporteurs créés ensuite. */
export function useSetGroupSharing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<GroupSharingSettings>) =>
      (await api.put<GroupSharingSettings>('/carriers/group-sharing', patch)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(SHARING_KEY, data);
      // Les lignes portent leur propre état de partage : la liste est à relire.
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
    },
  });
}
